import { Injectable } from '@angular/core';
import { Practitioner } from '../models/practitioner.model';
import {  Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AngularFirestore, DocumentSnapshot, DocumentData } from '@angular/fire/firestore';
import { AngularFireStorage } from "@angular/fire/storage";
import { environment } from 'src/environments/environment';
import algoliasearch, { SearchIndex } from 'algoliasearch';
import { Search } from '../models/search.model';
import { UploadTaskSnapshot, UploadTask } from '@angular/fire/storage/interfaces';
import { Testimonial } from '../models/testimonial.model';
import { TestimonialService } from './testimonial.service';
import { SearchResult } from '../models/search-result.model';
import { MailService } from './mail.service';
import { PractitionerStatus } from '../enums/practitioner-status.enum';
import { StatusService } from './status.service';
import { Status } from '../models/status.model';

@Injectable()
export class PractitionerService {
  private searchClient = algoliasearch(
    environment.algolia.appId,
    environment.algolia.apiKey
  );
  algoliaIndex: SearchIndex = this.searchClient.initIndex(environment.algolia.practitionerIndex);
  ipAddress: string = '';

  constructor(private firestore: AngularFirestore, private testimonialService: TestimonialService, private storage: AngularFireStorage, private mailService: MailService, private statusService: StatusService){}

  getPractitioners(): Observable<Practitioner[]> {
    return this.firestore.collection('practitioners').snapshotChanges().pipe(map(collection => {
      return collection.map(p => {
        return Practitioner.mapFromFirestore(p.payload.doc.data(), p.payload.doc.id)
      });
    }));
  }

  searchPractitioners(searchParameters: Search): Promise<SearchResult[]>  {
    return new Promise<SearchResult[]>((resolve, reject) => {
      this.algoliaIndex.search(searchParameters.query, {
        filters: this._buildFilter(searchParameters.insurance, searchParameters.language),
        getRankingInfo: true,
        aroundLatLngViaIP: (!searchParameters.location && (!searchParameters.longitude || !searchParameters.latitude)),
        aroundLatLng: (searchParameters.latitude && searchParameters.longitude) ? searchParameters.latitude + ', ' + searchParameters.longitude : ''}).then(response => {
        let results: SearchResult[] = response.hits.map(p => {
          return SearchResult.mapSearchResult(p, p.objectID);
        });
        resolve(results);
      }, error => {
        reject(error);
      })
    });
  }

  getPractitioner(id: string): Observable<Practitioner> {
    return this.firestore.collection('practitioners').doc(id).get().pipe(switchMap((document: DocumentSnapshot<DocumentData>) => {
      var practitioner = Practitioner.mapFromFirestore(document.data(), document.id);
      return this.testimonialService.getTestimonials(practitioner.id).pipe(map((testimonials: Testimonial[]) => {
        practitioner.testimonials = testimonials;
        return practitioner;
      }))
    }));
  }

  getUnapprovedPractitioners(): Promise<Practitioner[]> {
    return this.firestore.collection('practitioners').ref.where('approved', '==', false).get().then(snapshot => {
      var practitioners: Practitioner[] = [];
      snapshot.forEach(doc => {
        practitioners.push(Practitioner.mapFromFirestore(doc.data(), doc.id));
      });
      return practitioners;
    })
  }

  getPractitionersByStatus(status: string): Promise<Practitioner[]> {
    var query = status === PractitionerStatus.Any ?
      this.firestore.collection('practitioners').ref :
      this.firestore.collection('practitioners').ref.where('status', '==', status);
    return query.get().then(snapshot => {
      var practitioners: Practitioner[] = [];
      snapshot.forEach(doc => {
        practitioners.push(Practitioner.mapFromFirestore(doc.data(), doc.id));
      });
      return practitioners;
    })
  }

  approvePractitionerPendingUpdates(practitioner: Practitioner) {
    return new Promise<void>((resolve, reject) => {
      this.getPendingUpdates(practitioner.id).then(snapshot => {
        if (snapshot.size > 0) {
          let updates = snapshot.docs[0].data();
          let updateId = snapshot.docs[0].id;
          Object.keys(updates).forEach(key => {
            if (key !== 'approved' && key !== 'createdAt') {
              practitioner[key] = updates[key];
            }
          })
          this.firestore.collection('practitioners').doc(practitioner.id).update(JSON.parse(JSON.stringify(practitioner)));
          this.firestore.collection('practitioners').doc(practitioner.id).collection('pendingUpdates').doc(updateId).update({ approved: true });
          resolve();
        }
        reject();
      }, error => {
        reject();
      })
    });
  }

  updatePractitioner(practitioner: Practitioner, updates: any): Promise<void> {
     return new Promise<void>((resolve, reject) => {
      Object.keys(updates).forEach(key => {
        practitioner[key] = updates[key];
      });
      this.firestore.collection('practitioners').doc(practitioner.id).update(JSON.parse(JSON.stringify(practitioner)))
        .then(_ => resolve())
        .catch(_ => reject());
     });
  }

  // updatePractitionerPendingUpdates(practitioner: Practitioner, updates: any): Promise<void> {
  //   return new Promise<void>((resolve, reject) => [
  //     this.getPendingUpdates(practitioner.id).then(snapshot => {
  //       if (snapshot.size === 0) {
  //         updates.createdAt = new Date().getTime();
  //         updates.approved = true; // automatically approving updates
  //         this.firestore.collection('practitioners').doc(practitioner.id).collection('pendingUpdates').add(JSON.parse(JSON.stringify(updates))).then(_ => {
  //           var description = 'Practitioner has submitted ' + PractitionerStatus.UpdatesPendingApproval;
  //           var status = PractitionerStatus.UpdatesPendingApproval;
  //           var newStatus = new Status(
  //             new Date().getTime(), description, status, practitioner.id
  //           );
  //           this.statusService.createStatus(newStatus, practitioner);
  //           resolve();
  //         }, error => {
  //           console.log(error);
  //           reject('Unable to update profile at this time.');
  //         });
  //       } else {
  //         this.firestore.collection('practitioners').doc(practitioner.uid).collection('pendingUpdates').doc(snapshot.docs[0].id).update(JSON.parse(JSON.stringify(updates))).then(_ => {
  //           resolve(this.approvePractitionerPendingUpdates(practitioner));
  //         }, error => {
  //           console.log(error);
  //           reject('Unable to update profile at this time.');
  //         });
  //       }
  //     }, error => {
  //       console.log(error);
  //       reject('Unable to update profile at this time.');
  //     })
  //   ]);
  // }

  getPendingUpdates(practitionerId: string): Promise<DocumentData> {
    return this.firestore.collection('practitioners').doc(practitionerId).collection('pendingUpdates').ref.where('approved', '==', false).get();
  }

  createPractitioner(practitioner: Practitioner, image?: File): Promise<Practitioner> {
    let uid: string = this.firestore.collection('practitioners').ref.doc().id;
    return new Promise<Practitioner>((resolve, reject) => {
      this.uploadProfileImage(uid, image).then((url: string) => {
        practitioner.profileImage = url;
        this._createPractitioner(practitioner, uid).then((finalPractitioner: Practitioner) => {
          this.mailService.sendPractitionerCreationEmail(finalPractitioner);
          resolve(finalPractitioner);
        }, error => {
          reject(error);
        });
      }, _ => {
        // Error uploading profile photo.
        this._createPractitioner(practitioner, uid).then((finalPractitioner: Practitioner) => {
          resolve(finalPractitioner);
        }, error => {
          reject(error);
        });
      });
    });
  }

  private _createPractitioner(practitioner: Practitioner, uid: string): Promise<Practitioner> {
    return new Promise<Practitioner>((resolve, reject) => {
      let docRef = this.firestore.collection('practitioners').ref.doc(uid);
      docRef.set(practitioner.mapToFirestore());
      docRef.get().then(_ => {
        practitioner.id = docRef.id;
        resolve(practitioner);
      }, _ => {
        reject('There was a problem creating your account.');
      });
    });
  }

  uploadProfileImage(practitionerId: string, image?: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (!image) { resolve(null); }
      var uploadTask: UploadTask = this.storage.ref('profile-images').child(practitionerId + '-' + image.name).put(image);
      uploadTask.then((snapshot: UploadTaskSnapshot) => {
        snapshot.ref.getDownloadURL().then((url: string) => {
          resolve(url);
        });
      }, error => {
        reject(error);
      });
    });
  }

  private _buildFilter(insurance: string, language: string): string {
    var filter: string = '';

    if (insurance) {
      filter += 'insuranceType:' + insurance;
    }

    if (language) {
      if (insurance) { filter += ' AND '; }
      filter += 'languages:' + language;
    }

    if (language || insurance) {
      filter += ' AND ';
    }
    filter += 'isApproved:true';

    return filter;
  }
}
